import { useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Checkbox } from '../../../components/ui/checkbox';
import { Search, CalendarDays, X, Users, ChevronDown } from 'lucide-react';
import { CUSTOMERS } from '../utils/constants';
import { getMonthOptions } from '../utils/calculations';
import { useI18nStore } from '../../../lib/i18n';

export function RoutesFilters({
  selectedCustomers,
  selectedFirm,
  selectedMonths,
  searchQuery,
  firms,
  customerList,
  dateRange,
  onCustomerToggle,
  onFirmChange,
  onMonthsChange,
  onDateRangeChange,
  onSearchChange,
  t
}) {
  const { language } = useI18nStore();
  const [monthPopoverOpen, setMonthPopoverOpen] = useState(false);
  const [customerPopoverOpen, setCustomerPopoverOpen] = useState(false);
  const monthOptions = getMonthOptions(t, language).filter(o => o.value !== 'all');

  const hasDateRange = !!(dateRange?.start && dateRange?.end);
  const hasMonthFilter = selectedMonths.length > 0;

  // Use DB customers if available, fall back to hardcoded CUSTOMERS
  const customers = customerList && customerList.length > 0
    ? customerList.map(c => ({ id: c.slug, name: c.name, color: c.bg_color || 'bg-slate-100 text-slate-700', hex: c.color || '#6b7280' }))
    : CUSTOMERS.map(c => ({ ...c, hex: '#6b7280' }));

  const toggleMonth = (value) => {
    if (selectedMonths.includes(value)) {
      onMonthsChange(selectedMonths.filter(m => m !== value));
    } else {
      onMonthsChange([...selectedMonths, value]);
    }
  };

  const monthLabel = selectedMonths.length === 0
    ? (t('routes.allMonths') || 'Tüm Aylar')
    : selectedMonths.length === 1
    ? monthOptions.find(o => o.value === selectedMonths[0])?.label || selectedMonths[0]
    : `${selectedMonths.length} ay`;

  const selectAllCustomers = () => {
    customers.forEach(c => {
      if (!selectedCustomers.has(c.id)) onCustomerToggle(c.id);
    });
  };

  const clearAllCustomers = () => {
    const currentArr = Array.from(selectedCustomers);
    currentArr.forEach(id => onCustomerToggle(id));
    // Keep at least one selected
    if (customers.length > 0) onCustomerToggle(customers[0].id);
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-end">
          {/* Customer Multi-Select */}
          <div className="w-full lg:w-auto">
            <Label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">
              {t('routes.customer') || 'Müşteri'}
            </Label>
            <Popover open={customerPopoverOpen} onOpenChange={setCustomerPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full lg:w-[260px] h-auto min-h-[36px] justify-between font-normal px-3 py-1.5"
                  data-testid="customer-filter-trigger"
                >
                  <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
                    {selectedCustomers.size === 0 ? (
                      <span className="text-sm text-slate-400">Müşteri seçin...</span>
                    ) : selectedCustomers.size === customers.length ? (
                      <Badge variant="secondary" className="text-xs h-6">
                        <Users className="h-3 w-3 mr-1" />
                        Tüm Müşteriler ({customers.length})
                      </Badge>
                    ) : (
                      Array.from(selectedCustomers).slice(0, 4).map(id => {
                        const cust = customers.find(c => c.id === id);
                        return cust ? (
                          <Badge
                            key={id}
                            className={`text-xs h-6 ${cust.color}`}
                            style={{ borderLeft: `3px solid ${cust.hex}` }}
                          >
                            {cust.name}
                          </Badge>
                        ) : null;
                      })
                    )}
                    {selectedCustomers.size > 4 && (
                      <Badge variant="secondary" className="text-xs h-6">+{selectedCustomers.size - 4}</Badge>
                    )}
                  </div>
                  <ChevronDown className="h-4 w-4 shrink-0 ml-1 text-slate-400" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0" align="start">
                {/* Header */}
                <div className="flex items-center justify-between px-3 py-2.5 border-b bg-slate-50 dark:bg-slate-800">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Müşteri Seçimi</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-blue-600" onClick={selectAllCustomers}>
                      Tümü
                    </Button>
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-slate-400" onClick={clearAllCustomers}>
                      Temizle
                    </Button>
                  </div>
                </div>
                {/* Customer List */}
                <div className="py-1 max-h-[300px] overflow-y-auto">
                  {customers.map(cust => {
                    const isSelected = selectedCustomers.has(cust.id);
                    return (
                      <div
                        key={cust.id}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                          isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                        onClick={() => onCustomerToggle(cust.id)}
                        data-testid={`customer-option-${cust.id}`}
                      >
                        <Checkbox checked={isSelected} className="h-4 w-4" />
                        <div
                          className="w-3 h-3 rounded-full shrink-0 ring-1 ring-black/10"
                          style={{ backgroundColor: cust.hex }}
                        />
                        <span className="text-sm font-medium flex-1">{cust.name}</span>
                        {isSelected && (
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Footer */}
                <div className="px-3 py-2 border-t bg-slate-50 dark:bg-slate-800">
                  <span className="text-xs text-slate-400">{selectedCustomers.size} / {customers.length} seçili</span>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Firm Filter */}
          <div>
            <Label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">{t('routes.firm') || 'Firma'}</Label>
            <Select value={selectedFirm || "all"} onValueChange={(v) => onFirmChange(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[130px] h-9 text-sm">
                <SelectValue placeholder={t('routes.allFirms') || 'Tüm Firmalar'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('routes.allFirms') || 'Tüm Firmalar'}</SelectItem>
                {firms.map(firm => (
                  <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Month Filter - Multi Select */}
          <div className={hasDateRange ? 'opacity-40 pointer-events-none' : ''}>
            <Label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">{t('routes.month') || 'Ay'}</Label>
            <Popover open={monthPopoverOpen} onOpenChange={setMonthPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[150px] h-9 text-sm justify-between font-normal"
                  data-testid="month-filter-trigger"
                  disabled={hasDateRange}
                >
                  <div className="flex items-center gap-1.5 truncate">
                    <CalendarDays className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    <span className="truncate">{monthLabel}</span>
                  </div>
                  {selectedMonths.length > 0 && (
                    <span className="ml-1 bg-blue-100 text-blue-700 text-xs rounded-full px-1.5 py-0.5 shrink-0">
                      {selectedMonths.length}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-2" align="start">
                <div className="flex items-center justify-between mb-2 pb-2 border-b">
                  <span className="text-xs font-medium text-slate-500">{t('routes.selectMonths') || 'Ay Seçin'}</span>
                  {selectedMonths.length > 0 && (
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-slate-400 hover:text-red-500" onClick={() => onMonthsChange([])}>
                      <X className="h-3 w-3 mr-1" />
                      {t('common.clear') || 'Temizle'}
                    </Button>
                  )}
                </div>
                <div className="space-y-0.5 max-h-[280px] overflow-y-auto">
                  {monthOptions.map(option => (
                    <div
                      key={option.value}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                      onClick={() => toggleMonth(option.value)}
                      data-testid={`month-option-${option.value}`}
                    >
                      <Checkbox checked={selectedMonths.includes(option.value)} className="h-4 w-4" />
                      <span className="text-sm">{option.label}</span>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Date Range */}
          <div className="flex items-end gap-2">
            <div className={hasMonthFilter ? 'opacity-40 pointer-events-none' : ''}>
              <Label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">{t('common.startDate')}</Label>
              <Input
                type="date"
                className="w-[132px] h-9 text-sm pr-1"
                value={dateRange?.start || ''}
                disabled={hasMonthFilter}
                onChange={e => onDateRangeChange({ ...dateRange, start: e.target.value })}
                data-testid="date-range-start"
              />
            </div>
            <div className={hasMonthFilter ? 'opacity-40 pointer-events-none' : ''}>
              <Label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">{t('common.endDate')}</Label>
              <Input
                type="date"
                className="w-[132px] h-9 text-sm pr-1"
                value={dateRange?.end || ''}
                disabled={hasMonthFilter}
                onChange={e => onDateRangeChange({ ...dateRange, end: e.target.value })}
                data-testid="date-range-end"
              />
            </div>
            {hasDateRange && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-slate-400 hover:text-red-500"
                onClick={() => onDateRangeChange({ start: '', end: '' })}
                data-testid="date-range-clear"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Search */}
          <div>
            <Label className="text-xs text-slate-500 dark:text-slate-400 mb-2 block">{t('routes.search') || 'Ara'}</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t('routes.searchPlaceholder') || 'Şoför, plaka...'}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-9 w-[160px] h-9 text-sm"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
